import { useState } from "react";
import { supabase } from "../lib/supabaseClient";

// ─── Pakete ───────────────────────────────────────────────────────────────────

const PACKAGES = [
  {
    id: "starter",
    name: "Starter",
    chf: "1.90",
    credits: 300,
    planungen: 7,
  },
  {
    id: "standard",
    name: "Standard",
    chf: "4.90",
    credits: 850,
    planungen: 21,
    recommended: true,
  },
  {
    id: "premium",
    name: "Premium",
    chf: "9.90",
    credits: 2000,
    planungen: 50,
  },
];

const PLANNING_COST = 40; // SC pro Planung

// ─── Typen ────────────────────────────────────────────────────────────────────

interface Props {
  currentBalance: number | null;
  onClose: () => void;
}

// ─── Komponente ──────────────────────────────────────────────────────────────

export default function CreditPurchaseModal({ currentBalance, onClose }: Props) {
  const [selectedPackage, setSelectedPackage] = useState<string>("standard");
  const [customChf, setCustomChf] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const customCredits = (() => {
    const val = parseFloat(customChf.replace(",", "."));
    if (isNaN(val) || val < 1.5) return null;
    return Math.floor(val * 158);
  })();

  const canProceed = useCustom ? customCredits !== null : !!selectedPackage;

  const handleCheckout = async () => {
    setError("");
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError("Nicht angemeldet.");
        return;
      }

      const body: Record<string, unknown> = {
        user_id: session.user.id,
      };

      if (useCustom) {
        const val = parseFloat(customChf.replace(",", "."));
        body.custom_chf_rappen = Math.round(val * 100);
      } else {
        body.package_id = selectedPackage;
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY ?? "",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok || !data.url) {
        setError(data.error ?? "Fehler beim Erstellen der Checkout-Session.");
        return;
      }

      // Weiterleitung zu Stripe Checkout
      window.location.href = data.url;
    } catch {
      setError("Verbindungsfehler. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-[#161b27] border border-[#2a3347] w-full max-w-sm rounded-2xl shadow-xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#2a3347]">
          <div>
            <h2 className="text-base font-semibold text-slate-100">✨ Svevana Credits</h2>
            {currentBalance !== null && (
              <p className="text-xs text-slate-500 mt-0.5">
                Aktuelles Guthaben: <span className="text-slate-300 font-medium">{currentBalance} SC</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-300 text-xl transition-colors"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          {/* Info */}
          <div className="bg-[#1e2639] rounded-xl px-4 py-3 text-xs text-slate-400 space-y-1">
            <p className="font-medium text-slate-300">Was kannst du damit machen?</p>
            <p>• KI-Reiseplanung: <span className="text-slate-200">{PLANNING_COST} SC</span> pro Plan</p>
            <p>• 1 Plan ≈ CHF 0.20–0.25 je nach Paket</p>
          </div>

          {/* Pakete */}
          <div className="space-y-2">
            {PACKAGES.map((pkg) => (
              <button
                key={pkg.id}
                type="button"
                onClick={() => { setSelectedPackage(pkg.id); setUseCustom(false); }}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                  !useCustom && selectedPackage === pkg.id
                    ? "border-sky-500/50 bg-sky-500/10"
                    : "border-[#2a3347] bg-[#1e2639] hover:border-[#3a4357]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-100">{pkg.name}</span>
                      {pkg.recommended && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/20 text-sky-400 border border-sky-500/30">
                          Empfohlen
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{pkg.credits} SC · {pkg.planungen} Planungen</p>
                  </div>
                  <span className="text-base font-bold text-slate-100">CHF {pkg.chf}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Trennlinie */}
          <div className="flex items-center gap-3">
            <hr className="flex-1 border-[#2a3347]" />
            <span className="text-xs text-slate-600">oder</span>
            <hr className="flex-1 border-[#2a3347]" />
          </div>

          {/* Custom-Betrag */}
          <div>
            <label className="block mb-1.5 text-xs text-slate-400 font-medium">
              Eigener Betrag <span className="text-slate-600">(mind. CHF 1.50)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">CHF</span>
              <input
                type="number"
                min="1.50"
                step="0.50"
                value={customChf}
                onChange={(e) => { setCustomChf(e.target.value); setUseCustom(true); }}
                onFocus={() => setUseCustom(true)}
                placeholder="z.B. 3.00"
                className={`w-full pl-10 pr-3 py-2 rounded-xl border text-sm bg-[#1e2639] text-slate-100 placeholder:text-slate-600 outline-none transition-colors ${
                  useCustom ? "border-sky-500/50" : "border-[#2a3347]"
                } focus:border-sky-500/50`}
              />
            </div>
            {useCustom && customCredits !== null && (
              <p className="text-xs text-slate-500 mt-1">
                = <span className="text-slate-300 font-medium">{customCredits} SC</span> · {Math.floor(customCredits / PLANNING_COST)} Planungen
              </p>
            )}
            {useCustom && customChf && customCredits === null && (
              <p className="text-red-400 text-xs mt-1">Mindestbetrag ist CHF 1.50.</p>
            )}
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-[#2a3347]">
          <button
            onClick={handleCheckout}
            disabled={!canProceed || loading}
            className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? "Wird vorbereitet…" : "Weiter zu Stripe →"}
          </button>
          <p className="text-[10px] text-slate-600 text-center mt-2">
            Sichere Zahlung über Stripe · CHF · Keine wiederkehrenden Gebühren
          </p>
        </div>
      </div>
    </div>
  );
}
